import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { successToastMessage } from '../../../../components/layout/toast-root/toast';
import { useAuthErrorHandler } from '../../../../hooks/use-auth-error-handler';
import { useFeature } from '../../../../hooks/use-feature';
import { useFirebaseAuth } from '../../../../hooks/useFirebaseAuth';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { AppFeatureKey } from '../../../../shared/constants';
import {
  getChangePasswordFormValidation,
  getLinkEmailFormValidation,
} from '../change-password/FormikData';

type ProviderId = 'password' | 'google.com' | 'apple.com' | 'facebook.com';

interface IProviderInfo {
  providerId: ProviderId;
  linked: boolean;
  /** Email for this provider (main user email for password, from providerData for others). */
  email?: string | null;
  /** Display name from provider (Google, Apple, Facebook). */
  displayName?: string | null;
}

function getProviderDataFromUser(
  user: {
    email?: string | null;
    providerData?: Array<{
      providerId?: string;
      email?: string | null;
      displayName?: string | null;
    }>;
  } | null,
): IProviderInfo[] {
  const providerIds: ProviderId[] = [
    'password',
    'google.com',
    'apple.com',
    'facebook.com',
  ];
  const providerMap = new Map<
    string,
    { email?: string | null; displayName?: string | null }
  >();
  for (const p of user?.providerData ?? []) {
    if (p.providerId) {
      providerMap.set(p.providerId, {
        email: p.email ?? null,
        displayName: p.displayName ?? null,
      });
    }
  }
  return providerIds.map((providerId) => {
    const linked = providerMap.has(providerId);
    const details = providerMap.get(providerId);
    if (providerId === 'password' && linked) {
      return {
        providerId,
        linked: true,
        email: user?.email ?? details?.email ?? null,
        displayName: null,
      };
    }
    return {
      providerId,
      linked,
      email: details?.email ?? null,
      displayName: details?.displayName ?? null,
    };
  });
}

export function useLinkedAccountsComponent() {
  const { t } = useTranslation();
  const handleAuthError = useAuthErrorHandler();
  const {
    getCurrentUser,
    linkWithEmailAndPassword,
    linkWithPopup,
    changePassword,
  } = useFirebaseAuth();
  const { data: user } = useGetMyDetails();
  const {
    [AppFeatureKey.appleAuthorization]: appleAuthorizationActive,
    [AppFeatureKey.facebookAuthorization]: facebookAuthorizationActive,
  } = useFeature();

  const [providerData, setProviderData] = useState<IProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLinkEmailForm, setShowLinkEmailForm] = useState(false);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);

  const refreshProviders = useCallback(async () => {
    const fbUser = await getCurrentUser();
    setProviderData(getProviderDataFromUser(fbUser));
  }, [getCurrentUser]);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser().then((fbUser) => {
      if (cancelled) return;
      setProviderData(getProviderDataFromUser(fbUser));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // Initial load only; getCurrentUser ref is unstable and would cause infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkEmailValidationSchema = useMemo(
    () => getLinkEmailFormValidation(t),
    [t],
  );
  const changePasswordValidationSchema = useMemo(
    () => getChangePasswordFormValidation(t),
    [t],
  );

  const linkEmailFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: linkEmailValidationSchema,
    initialValues: { password: '', confirmPassword: '' },
    onSubmit: async (values) => {
      const email = user?.email;
      if (!email) return;
      try {
        await linkWithEmailAndPassword(email, values.password);
        successToastMessage(t('settings.successLinkEmail'));
        setShowLinkEmailForm(false);
        linkEmailFormik.resetForm();
        await refreshProviders();
      } catch (error: unknown) {
        console.error('Link email error: ', error);
        handleAuthError(error);
      }
    },
  });

  const changePasswordFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: changePasswordValidationSchema,
    initialValues: { currentPassword: '', password: '', confirmPassword: '' },
    onSubmit: async (values) => {
      const email = user?.email;
      if (!email) return;
      try {
        await changePassword(email, values.currentPassword, values.password);
        successToastMessage(t('settings.successChangePasswordMessage'));
        setShowChangePasswordForm(false);
        changePasswordFormik.resetForm();
        await refreshProviders();
      } catch (error: unknown) {
        console.error('Change password error: ', error);
        handleAuthError(error);
      }
    },
  });

  const handleLinkGoogle = async () => {
    try {
      await linkWithPopup('google');
      successToastMessage(t('settings.successLinkProvider'));
      await refreshProviders();
    } catch (error: unknown) {
      console.error('Link Google error: ', error);
      handleAuthError(error);
    }
  };

  const handleLinkApple = async () => {
    try {
      await linkWithPopup('apple');
      successToastMessage(t('settings.successLinkProvider'));
      await refreshProviders();
    } catch (error: unknown) {
      console.error('Link Apple error: ', error);
      handleAuthError(error);
    }
  };

  const handleLinkFacebook = async () => {
    try {
      await linkWithPopup('facebook');
      successToastMessage(t('settings.successLinkProvider'));
      await refreshProviders();
    } catch (error: unknown) {
      console.error('Link Facebook error: ', error);
      handleAuthError(error);
    }
  };

  const passwordProvider = providerData.find(
    (p) => p.providerId === 'password',
  );
  const googleProvider = providerData.find(
    (p) => p.providerId === 'google.com',
  );
  const appleProviderInfo = providerData.find(
    (p) => p.providerId === 'apple.com',
  );
  const facebookProviderInfo = providerData.find(
    (p) => p.providerId === 'facebook.com',
  );
  const isPasswordLinked = Boolean(passwordProvider?.linked);
  const isGoogleLinked = Boolean(googleProvider?.linked);
  const isAppleLinked = Boolean(appleProviderInfo?.linked);
  const isFacebookLinked = Boolean(facebookProviderInfo?.linked);

  return {
    loading,
    providerData,
    showLinkEmailForm,
    setShowLinkEmailForm,
    showChangePasswordForm,
    setShowChangePasswordForm,
    linkEmailFormik,
    changePasswordFormik,
    handleLinkGoogle,
    handleLinkApple,
    handleLinkFacebook,
    isPasswordLinked,
    isGoogleLinked,
    isAppleLinked,
    isFacebookLinked,
    appleAuthorizationActive,
    facebookAuthorizationActive,
    userEmail: user?.email,
    passwordProvider,
    googleProvider,
    appleProviderInfo,
    facebookProviderInfo,
  };
}
